/* eslint-disable no-duplicate-imports */
/**
 * Removed:
 *  - getCalendarContainer: use `getPopupContainer` instead
 *  - onOk
 *
 * New Feature:
 *  - picker
 *  - allowEmpty
 *  - selectable
 *
 * Tips: Should add faq about `datetime` mode with `defaultValue`
 */
import * as React from 'react';
import classNames from 'classnames';
import warning from 'rc-util/lib/warning';
import useMergedState from 'rc-util/lib/hooks/useMergedState';
import PickerPanel from './PickerPanel';
import PickerTrigger from './PickerTrigger';
import { formatValue, isEqual, parseValue } from './utils/dateUtil';
import getDataOrAriaProps, { toArray } from './utils/miscUtil';
import PanelContext from './PanelContext';
import { getDefaultFormat, getInputSize, elementsContains } from './utils/uiUtil';
import usePickerInput from './hooks/usePickerInput';
import useTextValueMapping from './hooks/useTextValueMapping';
import useValueTexts from './hooks/useValueTexts';
import useHoverValue from './hooks/useHoverValue';
function InnerPicker(props) {
    const { prefixCls = 'rc-picker', id, tabIndex, style, className, dropdownClassName, dropdownAlign, popupStyle, transitionName, generateConfig, locale, inputReadOnly, allowClear, autoFocus, showTime, picker = 'date', format, use12Hours, value, defaultValue, open, defaultOpen, defaultOpenValue, suffixIcon, clearIcon, disabled, disabledDate, placeholder, getPopupContainer, pickerRef, panelRender, onChange, onOpenChange, onFocus, onBlur, onMouseDown, onMouseUp, onMouseEnter, onMouseLeave, onContextMenu, onClick, onKeyDown, onSelect, direction, autoComplete = 'off', status, } = props;
    const inputRef = React.useRef(null);
    const needConfirmButton = (picker === 'date' && !!showTime) || picker === 'time';
    // ============================= State =============================
    const formatList = toArray(getDefaultFormat(format, picker, showTime, use12Hours));
    // Panel ref
    const panelDivRef = React.useRef(null);
    const inputDivRef = React.useRef(null);
    // Real value
    const [mergedValue, setInnerValue] = useMergedState(null, {
        value,
        defaultValue,
    });
    // Selected value
    const [selectedValue, setSelectedValue] = React.useState(mergedValue);
    // Operation ref
    const operationRef = React.useRef(null);
    // Open
    const [mergedOpen, triggerInnerOpen] = useMergedState(false, {
        value: open,
        defaultValue: defaultOpen,
        postState: postOpen => (disabled ? false : postOpen),
        onChange: newOpen => {
            if (onOpenChange) {
                onOpenChange(newOpen);
            }
            if (!newOpen && operationRef.current && operationRef.current.onClose) {
                operationRef.current.onClose();
            }
        },
    });
    // ============================= Text ==============================
    const [valueTexts, firstValueText] = useValueTexts(selectedValue, {
        formatList,
        generateConfig,
        locale,
    });
    const [text, triggerTextChange, resetText] = useTextValueMapping({
        valueTexts,
        onTextChange: newText => {
            const inputDate = parseValue(newText, {
                locale,
                formatList,
                generateConfig,
            });
            if (inputDate && (!disabledDate || !disabledDate(inputDate))) {
                setSelectedValue(inputDate);
            }
        },
    });
    // ============================ Trigger ============================
    const triggerChange = (newValue) => {
        setSelectedValue(newValue);
        setInnerValue(newValue);
        if (onChange && !isEqual(generateConfig, mergedValue, newValue)) {
            onChange(newValue, newValue ? formatValue(newValue, { generateConfig, locale, format: formatList[0] }) : '');
        }
    };
    const triggerOpen = (newOpen) => {
        if (disabled && newOpen) {
            return;
        }
        triggerInnerOpen(newOpen);
    };
    const forwardKeyDown = (e) => {
        if (mergedOpen && operationRef.current && operationRef.current.onKeyDown) {
            // Let popup panel handle keyboard
            return operationRef.current.onKeyDown(e);
        }
        /* istanbul ignore next */
        /* eslint-disable no-lone-blocks */
        {
            warning(false, 'Picker not correct forward KeyDown operation. Please help to fire issue about this.');
            return false;
        }
    };
    const onInternalMouseUp = (...args) => {
        if (onMouseUp) {
            onMouseUp(...args);
        }
        if (inputRef.current) {
            inputRef.current.focus();
            triggerOpen(true);
        }
    };
    // ============================= Input =============================
    const [inputProps, { focused, typing }] = usePickerInput({
        blurToCancel: needConfirmButton,
        open: mergedOpen,
        value: text,
        triggerOpen,
        forwardKeyDown,
        isClickOutside: target => !elementsContains([panelDivRef.current, inputDivRef.current], target),
        onSubmit: () => {
            if (disabledDate && disabledDate(selectedValue)) {
                return false;
            }
            triggerChange(selectedValue);
            triggerOpen(false);
            resetText();
            return true;
        },
        onCancel: () => {
            triggerOpen(false);
            setSelectedValue(mergedValue);
            resetText();
        },
        onKeyDown: (e, preventDefault) => {
            onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e, preventDefault);
        },
        onFocus,
        onBlur,
    });
    // ============================= Sync ==============================
    // Close should sync back with text value
    React.useEffect(() => {
        if (!mergedOpen) {
            setSelectedValue(mergedValue);
            if (!valueTexts.length || valueTexts[0] === '') {
                triggerTextChange('');
            }
            else if (firstValueText !== text) {
                resetText();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mergedOpen, valueTexts]);
    // Change picker should sync back with text value
    React.useEffect(() => {
        if (!mergedOpen) {
            resetText();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [picker]);
    // Sync innerValue with control mode
    React.useEffect(() => {
        // Sync select value
        setSelectedValue(mergedValue);
    }, [mergedValue]);
    // ============================ Private ============================
    if (pickerRef) {
        pickerRef.current = {
            focus: () => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            },
            blur: () => {
                if (inputRef.current) {
                    inputRef.current.blur();
                }
            },
        };
    }
    const [, onEnter, onLeave] = useHoverValue(text, {
        formatList,
        generateConfig,
        locale,
    });
    // ============================= Panel =============================
    const panelProps = Object.assign(Object.assign({}, props), { className: undefined, style: undefined, pickerValue: undefined, onPickerValueChange: undefined, onChange: null });
    let panelNode = (<PickerPanel {...panelProps} generateConfig={generateConfig} className={classNames({
            [`${prefixCls}-panel-focused`]: !typing,
        })} value={selectedValue} locale={locale} tabIndex={-1} onSelect={date => {
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(date);
            setSelectedValue(date);
        }} direction={direction} onPanelChange={(viewDate, mode) => {
            const { onPanelChange } = props;
            onLeave(true);
            onPanelChange === null || onPanelChange === void 0 ? void 0 : onPanelChange(viewDate, mode);
        }}/>);
    if (panelRender) {
        panelNode = panelRender(panelNode);
    }
    const panel = (<div className={`${prefixCls}-panel-container`} onMouseDown={e => {
            e.preventDefault();
        }}>
            {panelNode}
        </div>);
    let suffixNode;
    if (suffixIcon) {
        suffixNode = <span className={`${prefixCls}-suffix`}>{suffixIcon}</span>;
    }
    let clearNode;
    if (allowClear && mergedValue && !disabled) {
        clearNode = (<span onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
            }} onMouseUp={e => {
                e.preventDefault();
                e.stopPropagation();
                triggerChange(null);
                triggerOpen(false);
            }} className={`${prefixCls}-clear`} role="button">
                {clearIcon || <span className={`${prefixCls}-clear-btn`}/>}
            </span>);
    }
    // ============================ Warning ============================
    if (process.env.NODE_ENV !== 'production') {
        warning(!defaultOpenValue, '`defaultOpenValue` may confuse user for the current value status. Please use `defaultValue` instead.');
    }
    // ============================ Return =============================
    const onContextSelect = (date, type) => {
        if (type === 'submit' || (type !== 'key' && !needConfirmButton)) {
            // triggerChange will also update selected values
            triggerChange(date);
            triggerOpen(false);
        }
    };
    const popupPlacement = direction === 'rtl' ? 'bottomRight' : 'bottomLeft';
    return (<PanelContext.Provider value={{
            operationRef,
            hideHeader: picker === 'time',
            panelRef: panelDivRef,
            onSelect: onContextSelect,
            open: mergedOpen,
            defaultOpenValue,
            onDateMouseEnter: onEnter,
            onDateMouseLeave: onLeave,
        }}>
            <PickerTrigger visible={mergedOpen} popupElement={panel} popupStyle={popupStyle} prefixCls={prefixCls} dropdownClassName={dropdownClassName} dropdownAlign={dropdownAlign} getPopupContainer={getPopupContainer} transitionName={transitionName} popupPlacement={popupPlacement} direction={direction}>
                <div className={classNames(prefixCls, className, {
            [`${prefixCls}-disabled`]: disabled,
            [`${prefixCls}-focused`]: focused,
            [`${prefixCls}-rtl`]: direction === 'rtl',
            [`${prefixCls}-status-${status}`]: status,
        })} style={style} onMouseDown={onMouseDown} onMouseUp={onInternalMouseUp} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onContextMenu={onContextMenu} onClick={onClick}>
                    <div className={classNames(`${prefixCls}-input`, {
            [`${prefixCls}-input-has-value`]: text.length,
            [`${prefixCls}-clear-disabled`]: !allowClear,
        })} ref={inputDivRef}>
                        <input id={id} tabIndex={tabIndex} disabled={disabled} readOnly={inputReadOnly || typeof formatList[0] === 'function' || !typing} value={text} onChange={e => {
            triggerTextChange(e.target.value);
        }} autoFocus={autoFocus} placeholder={placeholder} ref={inputRef} title={text} {...inputProps} size={getInputSize(picker, formatList[0], generateConfig)} {...getDataOrAriaProps(props)} autoComplete={autoComplete}/>
                        {clearNode}
                        {suffixNode}
                    </div>
                </div>
            </PickerTrigger>
        </PanelContext.Provider>);
}
// Wrap with class component to enable pass generic with instance method
class Picker extends React.Component {
    constructor() {
        super(...arguments);
        this.pickerRef = React.createRef();
        this.focus = () => {
            if (this.pickerRef.current) {
                this.pickerRef.current.focus();
            }
        };
        this.blur = () => {
            if (this.pickerRef.current) {
                this.pickerRef.current.blur();
            }
        };
    }
    render() {
        return (<InnerPicker {...this.props} pickerRef={this.pickerRef}/>);
    }
}
export default Picker;
